import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/api/client';
import { Button } from '../ui/Button';

interface SGAReportListItem {
  report_id: string;
  session_id: string;
  overall_score?: number;
  hire_recommendation?: string;
  created_at?: string;
}

export const SGAReportsPage: React.FC<{ candidateId?: string } > = ({ candidateId }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState<SGAReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('RecruitEdge Analysis Reports');
  const [body, setBody] = useState('Please find the combined analysis report attached.');
  const [sending, setSending] = useState(false);

  const resolvedCandidateId = candidateId || user?.id || '';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiClient.getSGAReportsByCandidate(resolvedCandidateId);
        setReports(data || []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    if (resolvedCandidateId) load();
  }, [resolvedCandidateId]);

  const downloadPdf = async (reportId: string) => {
    try {
      const blob = await apiClient.downloadSGAReportPdf(resolvedCandidateId, reportId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sga_report_${reportId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const openEmailDialog = async () => {
    try {
      setToEmail((user as any)?.email || '');
      // Prefill via AI suggestion
      const suggestion = await apiClient.suggestCombinedReportEmail(resolvedCandidateId, user?.id || '', 'client');
      setSubject(suggestion.subject || subject);
      setBody(suggestion.text_body || body);
    } catch (e) {
      // ignore and keep defaults
    } finally {
      setShowEmail(true);
    }
  };

  const sendEmail = async () => {
    try {
      setSending(true);
      await apiClient.sendCombinedReportEmail({
        candidate_id: resolvedCandidateId,
        recruiter_id: user?.id || '',
        to_email: toEmail,
        subject,
        html_body: `<p>${body}</p>`,
        text_body: body,
      });
      setShowEmail(false);
    } catch (e: any) {
      setError(e?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  return (
    <>
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-semibold">SGA Reports</h2>
      <div>
        <Button variant="secondary" onClick={openEmailDialog}>Email Combined Report</Button>
      </div>
      <div className="space-y-3">
        {reports.length === 0 && (
          <div className="text-sm text-gray-500">No reports found.</div>
        )}
        {reports.map((r) => (
          <div key={r.report_id} className="flex items-center justify-between bg-white/60 dark:bg-black/30 rounded-lg p-4 border border-gray-200/50">
            <div className="space-y-1">
              <div className="text-sm font-medium">Report #{r.report_id.slice(0,8)}</div>
              <div className="text-xs text-gray-600">Score: {r.overall_score ?? '-'} | Recommendation: {r.hire_recommendation ?? '-'}</div>
              <div className="text-xs text-gray-500">Session: {r.session_id} | {r.created_at?.split('T')[0] ?? ''}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => downloadPdf(r.report_id)}>Download PDF</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
    {showEmail && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 w-full max-w-lg space-y-3 border border-gray-200/50">
          <h3 className="text-lg font-medium">Send Combined Report</h3>
          <div className="grid gap-3">
            <input className="px-3 py-2 rounded border" placeholder="Recipient email" value={toEmail} onChange={(e)=>setToEmail(e.target.value)} />
            <input className="px-3 py-2 rounded border" placeholder="Subject" value={subject} onChange={(e)=>setSubject(e.target.value)} />
            <textarea className="px-3 py-2 rounded border h-28" placeholder="Email body" value={body} onChange={(e)=>setBody(e.target.value)} />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={()=>setShowEmail(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending || !toEmail}>{sending ? 'Sending...' : 'Send Email'}</Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SGAReportsPage;


